import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, Building2, UserCheck, UserX, CalendarClock, Wallet, Banknote, AlertTriangle, Receipt } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { money } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AXIS_TICK = { fontSize: 11 };
const AXIS_TICK_SM = { fontSize: 10 };
const LINE_DOT = { r: 3 };
const BAR_RADIUS = [6, 6, 0, 0];

function Stat({ icon: Icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-900 text-white",
    blue: "bg-blue-600 text-white",
    emerald: "bg-emerald-500 text-white",
    orange: "bg-orange-500 text-white",
    red: "bg-red-500 text-white",
    purple: "bg-purple-500 text-white",
  };
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200 wp-fade-up">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-2xl font-head font-extrabold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [branch, setBranch] = useState("all");
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  const { data } = useQuery({
    queryKey: ["admin-dash", branch],
    queryFn: async () => (await api.get(`/dashboard/admin?branch_id=${branch}`)).data,
    refetchInterval: 30000,
  });

  const activeBranches = (branches || []).filter((b) => b.active);
  const selectedName = branch === "all" ? "All Branches" : activeBranches.find((b) => b.id === branch)?.name || "";

  if (!data) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm">{data.date} · {selectedName}</p>
        </div>
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger data-testid="dashboard-branch-filter" className="w-56 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="branch-filter-all">All Branches</SelectItem>
            {activeBranches.map((b) => (
              <SelectItem key={b.id} value={b.id} data-testid={`branch-filter-${b.code}`}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total Employees" value={data.total_employees} tone="slate" />
        <Stat icon={UserCheck} label="Present Today" value={data.present_today} tone="emerald" />
        <Stat icon={UserX} label="Absent Today" value={data.absent_today} tone="red" />
        <Stat icon={CalendarClock} label="On Leave Today" value={data.on_leave_today} tone="purple" />
        <Stat icon={UserCheck} label="Half Day Today" value={data.half_day_today} tone="orange" />
        {branch === "all" && <Stat icon={Building2} label="Total Branches" value={data.branch_count} tone="blue" />}
        <Stat icon={Wallet} label="Month Payable" value={money(data.month_payable)} tone="slate" />
        <Stat icon={Banknote} label="Advance Outstanding" value={money(data.total_advance_outstanding)} tone="orange" />
        <Stat icon={Receipt} label="Reimbursement Due" value={money(data.reimbursement_outstanding || 0)} tone="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-white rounded-2xl p-5 border border-slate-200">
          <h3 className="font-head font-bold text-slate-900 mb-4">7-Day Attendance Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={AXIS_TICK} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} />
              <Tooltip />
              <Line type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2.5} dot={LINE_DOT} name="Present" />
              <Line type="monotone" dataKey="half_day" stroke="#f97316" strokeWidth={2.5} dot={LINE_DOT} name="Half Day" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-4 bg-white rounded-2xl p-5 border border-slate-200">
          <h3 className="font-head font-bold text-slate-900 mb-4">Present + Half Day Today</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.branch_wise}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="branch" tick={AXIS_TICK_SM} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} />
              <Tooltip />
              <Bar dataKey="count" fill="#2563eb" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Branch-wise Today detailed */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200">
        <h3 className="font-head font-bold text-slate-900 mb-4">Branch-wise Today</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="text-left px-4 py-3">Branch</th>
              <th className="text-left px-4 py-3">Total Employees</th>
              <th className="text-left px-4 py-3">Present</th>
              <th className="text-left px-4 py-3">Half Day</th>
              <th className="text-left px-4 py-3">Absent</th>
              <th className="text-left px-4 py-3">On Leave</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {(data.branch_wise || []).length === 0 && <tr><td colSpan="6" className="px-4 py-4 text-slate-400">No branches.</td></tr>}
              {(data.branch_wise || []).map((b) => (
                <tr key={b.branch_id} data-testid={`branch-today-${b.branch_id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{b.branch}</td>
                  <td className="px-4 py-3">{b.total}</td>
                  <td className="px-4 py-3 text-emerald-600 font-semibold">{b.present}</td>
                  <td className="px-4 py-3 text-orange-600 font-semibold">{b.half_day}</td>
                  <td className="px-4 py-3 text-red-600 font-semibold">{b.absent}</td>
                  <td className="px-4 py-3 text-purple-600 font-semibold">{b.on_leave}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-red-50 rounded-2xl p-5 border border-red-200">
        <h3 className="font-head font-bold text-red-800 flex items-center gap-2 mb-3">
          <AlertTriangle className="h-5 w-5" /> Not Marked Today ({data.not_marked.length})
        </h3>
        {data.not_marked.length === 0 ? (
          <p className="text-sm text-red-600">Everyone has marked attendance. 🎉</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.not_marked.map((e, i) => (
              <div key={e.id} data-testid={`not-marked-employee-${i}`} className="bg-white rounded-lg px-3 py-2 border border-red-100 text-sm">
                <span className="font-medium text-slate-900">{e.name}</span>
                <span className="text-slate-400 ml-2">{e.employee_id}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
